import { getContext, setContext } from "svelte"
import { derived, writable } from "svelte/store"

const CAMERA_PERMISSION_CONTEXT_KEY = Symbol("cameraPermissionStore")

type CameraPermissionStore = ReturnType<typeof createCameraPermissionStore>

export const initCameraPermissionStore = (params?: PermissionStoreParams) =>
  setContext(CAMERA_PERMISSION_CONTEXT_KEY, createCameraPermissionStore(params))
export const getCameraPermissionStore = () =>
  getContext<CameraPermissionStore>(CAMERA_PERMISSION_CONTEXT_KEY)

type PermissionStoreParams = {
  /** Forces specific initial state. Used for debug/stories */
  __initialValue?: PermissionState
}

const createCameraPermissionStore = ({ __initialValue }: PermissionStoreParams = {}) => {
  /**
   * In mobile safari, permission's onchange doesn't work and the only way to track the camera permission state is by manually updating it, as any other approach to listen to the permission change event doesn't work.
   * If onchange event is triggered once, we mark it as working and rely on it, ignoring all subsequent manual update calls.
   *
   * Approaches to track permissions state that have been tried:
   * 'permission.onchange' event on the permission object - doesn't work, isn't triggered even once
   * 'track.onended' event on the video element - doesn't work, the track continues to "play" even if the camera permission has been revoked
   * 'track.readyState' polling - track always has 'live' state, even if the camera permission has been revoked
   * 'video.readyState' polling - same as track.readyState
   */
  let isPermissionTrackedByOnChange = false

  const store = writable<PermissionState>("prompt", (set) => {
    if (!("permissions" in navigator)) return

    const permission = getCameraPermission()
      .then((permission) => {
        if (!permission) return permission

        permission.onchange = () => {
          isPermissionTrackedByOnChange = true
          set(permission.state)
        }

        set(__initialValue ?? permission.state)

        return permission
      })
      .catch(() => null)

    return () =>
      permission.then((permission) => {
        if (permission) permission.onchange = null
      })
  })

  /**
   * @param expectedState - pass it when you expect the permission state to change to this value, as the permission API is not reliable enough and works poorly in Safari. Any current state other than `prompt` will still be considered as the final state and the `expectedState` will be ignored.
   */
  const refresh = async ({ expectedState }: { expectedState?: PermissionState } = {}) => {
    if (isPermissionTrackedByOnChange || !("permissions" in navigator)) return

    try {
      const { state } = await getCameraPermission()
      /**
       * In Safari, if the initial permission was denied, the permission state will still be `prompt`.
       * To fix it, when we are sure that the state is not `prompt` anymore, we can pass the expected state.
       */
      store.set(state === "prompt" && expectedState ? expectedState : state)
    } catch (e) {
      if (expectedState) store.set(expectedState)
    }
  }

  return {
    ...derived(store, ($store) => $store),
    refresh,
  }
}

const getCameraPermission = () => navigator.permissions.query({ name: "camera" as PermissionName })
